import { ContentLayout } from 'layouts/ContentLayout/ContentLayout';
import PageTitle from 'components/PageTitle';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RequestSupportFormType, RequestSupportSchema } from './schema';
import { useMutation, useQueryClient } from 'react-query';
import showToast from 'components/Toast/Toast';
import { postRequestSupport } from './utils';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { GraniteSelect } from 'components/Select/Select';
import GraniteTextArea from '../../components/TextArea/TextArea';
import PhoneNumberInput from 'components/V2/Input/PhoneNumber';
import { useMemo } from 'react';
import { useAuthUser } from 'hooks/useAuthUser';

const RequestSupport = () => {
  const _breadcrumbs = [
    {
      icon: 'home',
      label: 'Support',
      onClick: () => navigate('/request-support'),
    },
  ];

  const user = useAuthUser();
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const onBack = () => navigate('..');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RequestSupportFormType>({
    resolver: zodResolver(RequestSupportSchema),
  });

  const { mutate, isLoading } = useMutation(postRequestSupport, {
    onSuccess: () => {
      showToast.confirmation({
        message: `Request submitted`,
      });
      navigate('/request-support');
      queryClient.invalidateQueries('support-request-index-table');
    },
  });

  const priorityOptions = useMemo(
    () =>
      ['Low', 'Medium', 'High'].map((option) => ({
        label: option,
        value: option,
      })),
    [],
  );

  const onSubmit = (data: RequestSupportFormType) =>
    mutate({ ...data, company_name: user.companyName });

  return (
    <ContentLayout className="min-h-screen !justify-start">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col">
        <div className="mb-2 flex flex-col justify-between gap-2 md:flex-row md:items-center">
          <PageTitle title="Customer inquiry form" breadcrumbs={_breadcrumbs} />
          <div className="flex gap-4">
            <GraniteButton
              size="large"
              variant="secondary"
              className="w-full md:w-max"
              onClick={onBack}
            >
              Cancel
            </GraniteButton>
            <GraniteButton
              onClick={() => {}}
              disabled={isLoading}
              type="submit"
              size="large"
              className="w-full md:w-max"
            >
              Submit request
            </GraniteButton>
          </div>
        </div>
        <div className="flex h-full flex-1 flex-col flex-wrap justify-between overflow-hidden pt-12 xl:flex-row">
          <div className="w-full rounded-tl rounded-tr bg-background-base-surface-3 px-8 py-6 xl:max-w-[564px] xl:rounded-bl xl:rounded-tr-none">
            <p className="mb-8 text-2xl font-bold text-content-base-default">
              Customer information
            </p>
            <div className="space-y-6">
              <GraniteInput
                {...register('subject')}
                label="Subject"
                error={errors.subject?.message}
                placeholder="E.g., Question about latest invoice"
              />
              <GraniteInput
                {...register('contact_email')}
                label="Contact email"
                error={errors.contact_email?.message}
                placeholder="johndoe@email.com"
              />
              <PhoneNumberInput
                name="contact_phone_number"
                label="Contact phone number"
                control={control}
                error={errors.contact_phone_number?.message}
                placeholder="+1 (---) --- ----"
              />
            </div>
          </div>
          <div className="flex-1 rounded-bl rounded-br bg-background-base-surface-2 px-8 py-6 xl:rounded-bl-none  xl:rounded-tr">
            <p className="text-2xl font-bold text-content-base-default">
              General information
            </p>
            <div className="mt-8 w-full space-y-6">
              <Controller
                name="priority"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <GraniteSelect
                    {...field}
                    options={priorityOptions}
                    className="w-full"
                    placeholder="Select priority"
                    onChange={(value) => onChange(value?.value)}
                    label="Priority"
                    value={priorityOptions.find(
                      (option) => option.value === value,
                    )}
                    error={errors.priority?.message}
                  />
                )}
              />
              <GraniteTextArea
                {...register('description')}
                label="Description"
                error={errors.description?.message}
                inputClassName="resize-y !min-h-[150px]"
              />
            </div>
          </div>
        </div>
      </form>
    </ContentLayout>
  );
};

export default RequestSupport;
